import { z } from "zod";
import { MISSION_MODES, PlayInput, TIERS } from "./missions.js";
/**
 * GAME SETTINGS AND ACTIONS — what the Host chooses and what players can do.
 */
export const PACES = ["quick", "standard", "relaxed"];
export const GameSettings = z.object({
    pace: z.enum(PACES).default("standard"),
    /** The Endgame begins when this many players or fewer remain. */
    endgameAt: z.number().int().min(3).max(6).nullable().default(null),
    /** Override the automatic Traitor count (1 per 4 players, minimum 2 from 8). */
    traitorCount: z.number().int().min(1).max(5).nullable().default(null),
    /** Family mode: softer content. Rules are identical. */
    familyMode: z.boolean().default(false),
    /** How missions are played: in the room, as phone challenge rounds, or a mix. */
    missionMode: z.enum(MISSION_MODES).default("mix"),
    /**
     * As in the show, day one has no Round Table: the guests arrive, play the first
     * mission and record their diaries, and the first murder comes that night.
     * It makes a game of 8–10 last four or five days rather than two or three.
     */
    quietFirstDay: z.boolean().default(true),
    /** After a Traitor is banished, the Traitors may recruit a Faithful instead of murdering. */
    recruitment: z.boolean().default(true),
    /** Chances to win a Shield, decided by each guest on their phone (1 to 3 a game). */
    shields: z.boolean().default(true),
    /** Welcome Drinks before the first mission: the Hot Seat and Most Likely To. */
    gathering: z.boolean().default(true),
    /** Guests sent to the diary room each day; 0 sends everyone. */
    diarists: z.number().int().min(0).max(16).default(2),
});
/** Standard phase lengths in seconds. Pace scales them. Null = no timer. */
export const PHASE_SECONDS = {
    lobby: null,
    "role-reveal": 25,
    /** Worked out from the number of guests (see gatheringMs); this is a fallback. */
    gathering: 300,
    breakfast: 35,
    mission: 360,
    shield: 40,
    diaries: 150,
    "round-table": 360,
    vote: 75,
    banishment: 30,
    night: 90,
    endgame: 75,
    finale: null,
};
export const PACE_FACTOR = { quick: 0.6, standard: 1, relaxed: 1.4 };
export function phaseDurationMs(phase, pace) {
    const seconds = PHASE_SECONDS[phase];
    return seconds === null ? null : Math.round(seconds * PACE_FACTOR[pace] * 1000);
}
/**
 * Ceremony timing for the banishment: the slates are revealed one by one, the
 * verdict lands, the banished player gets their last words, then the reveal.
 * The server times the reveal cue with this, and the rules stretch the
 * banishment phase so the ceremony always fits — at any pace.
 */
export const CEREMONY = {
    // Each slate held long enough for a gasp: the reveal is the drama.
    slateMs: 2000,
    verdictMs: 4000,
    lastWordsMs: 10_000,
    revealSceneMs: 11_000,
    afterRevealMs: 4000,
};
/** Reasons a player can give at the Round Table. Chosen, not typed, so the TV can always show them. */
export const ACCUSE_REASONS = [
    "Too quiet",
    "Too helpful",
    "Sabotaged the mission",
    "Voted strangely",
    "Defended an Unfaithful",
    "Changed their story",
    "Just a feeling",
];
/** What the phone offers: a few clear reasons, and room to write your own. */
export const ACCUSE_CHOICES = ["Too quiet", "Too helpful", "Voted strangely", "Changed their story", "Just a feeling"];
/** Breakfast walk-in: when the first survivor appears, and the gap between them. */
export const BREAKFAST_WALK_IN = { startMs: 2600, perGuestMs: 450 };
export function banishmentRevealDelayMs(voteCount) {
    return CEREMONY.verdictMs + voteCount * CEREMONY.slateMs + CEREMONY.lastWordsMs;
}
export function banishmentMinMs(voteCount) {
    return banishmentRevealDelayMs(voteCount) + CEREMONY.revealSceneMs + CEREMONY.afterRevealMs;
}
export const MISSION_OUTCOMES = ["success", "partial", "fail"];
const playerId = z.string().min(1).max(64);
/**
 * Everything a player (or the Host Screen) can do once a game exists.
 * Validated on the server; the rules in packages/core decide whether it's allowed now.
 */
export const GameAction = z.discriminatedUnion("type", [
    /** Host: begin the game from the lobby. */
    z.object({ type: z.literal("start"), settings: GameSettings.optional() }),
    /** Host: end the current phase now (skip the timer). */
    z.object({ type: z.literal("advance") }),
    /** Host: freeze every clock, or start them again. */
    z.object({ type: z.literal("pause") }),
    z.object({ type: z.literal("resume") }),
    /** Host: give the current phase (or mission stage) more time. */
    z.object({ type: z.literal("extend"), seconds: z.number().int().min(10).max(300).default(60) }),
    /** Anyone alive: "I've seen my role" during the reveal. */
    z.object({ type: z.literal("ready") }),
    /** Traitor: secretly sabotage today's mission. */
    z.object({ type: z.literal("mission-sabotage") }),
    /** Host: score a room mission by tier (or the older outcome), and optionally award the Shield. */
    z.object({
        type: z.literal("mission-report"),
        tier: z.enum(TIERS).nullable().default(null),
        outcome: z.enum(MISSION_OUTCOMES).nullable().default(null),
        shieldTo: playerId.nullable().default(null),
    }),
    /** Phone mission: anything a player does in it (answer, guess, place a tile, ready…). */
    z.object({ type: z.literal("mission-input"), input: PlayInput }),
    /** Anyone alive: diary recorded (the video id arrives in Step 4). */
    z.object({ type: z.literal("diary-done"), mediaId: z.string().max(200).nullable().default(null) }),
    /** Anyone alive, at the Round Table: say who you suspect, and why. */
    z.object({
        type: z.literal("accuse"),
        targetId: playerId,
        // One of the short reasons, or the guest's own words (a line, no more).
        reason: z.string().trim().min(1).max(60).transform((r) => r.replace(/\s+/g, " ")).default("Just a feeling"),
    }),
    /** Welcome Drinks: do you believe the guest in the Hot Seat? */
    z.object({ type: z.literal("gather-believe"), index: z.number().int().min(0).max(20), believe: z.boolean() }),
    /** Welcome Drinks: who is most likely to…? */
    z.object({ type: z.literal("gather-pick"), index: z.number().int().min(0).max(5), targetId: playerId }),
    /** Round Table, the guest with the floor: put a question to the table, or to one guest. */
    z.object({ type: z.literal("ask"), questionId: z.string().min(1).max(40), targetId: playerId.nullable().default(null) }),
    /** Round Table: the guest with the floor hands it on early. */
    z.object({ type: z.literal("pass-floor") }),
    /** Anyone alive: vote to banish someone at the Round Table. */
    z.object({ type: z.literal("vote"), targetId: playerId }),
    /** Traitor: choose tonight's victim. */
    z.object({ type: z.literal("murder-vote"), targetId: playerId }),
    /** Night, everyone: choice made, candle lit, asleep. The night waits for every guest. */
    z.object({ type: z.literal("sleep") }),
    /** The Faithful the Traitors want to recruit: join them, or refuse. */
    z.object({ type: z.literal("recruit-answer"), accept: z.boolean() }),
    /** A Shield chance: reach in / claim it / take the deal (true), or not (false). */
    z.object({ type: z.literal("shield-choice"), take: z.boolean() }),
    /** Finalist: end the game, or banish again? */
    z.object({ type: z.literal("endgame-vote"), choice: z.enum(["end", "banish"]) }),
]);
/**
 * THE FLOOR at the Round Table. The Keeper hands the floor round the table:
 * each living guest gets a turn to speak, in seat order (starting somewhere
 * different each day), and the last turn is an open floor for everyone.
 * Worked out from the clock alone, so every phone and the TV agree.
 */
export function floorAt(aliveInSeatOrder, day, phaseEndsAt, totalMs, now) {
    const n = aliveInSeatOrder.length;
    if (n === 0 || phaseEndsAt === null || !totalMs)
        return null;
    const shift = day % n;
    const order = [...aliveInSeatOrder.slice(shift), ...aliveInSeatOrder.slice(0, shift)];
    const slot = totalMs / (n + 1);
    const start = phaseEndsAt - totalMs;
    const turn = Math.max(0, Math.min(n, Math.floor((now - start) / slot)));
    return {
        speaker: turn < n ? order[turn] : null,
        turn,
        turns: n + 1,
        turnEndsAt: start + (turn + 1) * slot,
        nextSpeaker: turn + 1 < n ? order[turn + 1] : null,
    };
}
export const FLOOR_TIMING = { turnSec: 30, openSec: 90, perDiarySec: 22 };
export function floorNow(plan, phaseEndsAt, now) {
    if (!plan || phaseEndsAt === null || plan.order.length === 0)
        return null;
    const start = phaseEndsAt - plan.firstBeforeEnd;
    const n = plan.order.length;
    // Before the first turn: today's diaries are playing.
    if (now < start)
        return { speaker: null, turn: -1, turns: n + 1, turnEndsAt: start, nextSpeaker: plan.order[0], waiting: true };
    let cursor = start;
    for (let i = 0; i < n; i++) {
        let end = cursor + plan.slotMs;
        const pass = plan.passes.find((p) => p.turn === i);
        if (pass)
            end = Math.min(end, Math.max(cursor, phaseEndsAt - pass.beforeEnd));
        if (now < end)
            return { speaker: plan.order[i], turn: i, turns: n + 1, turnEndsAt: end, nextSpeaker: i + 1 < n ? plan.order[i + 1] : null, waiting: false };
        cursor = end;
    }
    return { speaker: null, turn: n, turns: n + 1, turnEndsAt: phaseEndsAt, nextSpeaker: null, waiting: false };
}
//# sourceMappingURL=game.js.map